import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Users, Flame, CheckCircle2, TrendingUp, Brain, Sparkles, Loader2, ArrowUpRight, ArrowDownRight, Target, Trash2 } from "lucide-react";
import { ReferralEngine } from "../components/ReferralEngine";
import { SalesRecruitment } from "../components/SalesRecruitment";

const Funnel = ({ label, value, accent }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121824] p-5 kz-fade-up">
    <div className={`kz-head text-3xl font-extrabold ${accent}`}>{value}</div>
    <div className="text-xs text-slate-400 mt-1 font-medium">{label}</div>
  </div>
);

export const SalesLearning = () => {
  const [data, setData] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [refKey, setRefKey] = useState(0);

  const load = () => api.getSales().then(setData);
  useEffect(() => { load(); }, []);

  const runReview = async () => {
    setReviewing(true);
    try { await api.runReview(); await load(); toast.success("KURT finished his self-review."); }
    catch { toast.error("Review failed."); }
    finally { setReviewing(false); }
  };

  if (!data) return <div className="max-w-6xl mx-auto h-40 animate-pulse bg-white/5 rounded-2xl" />;
  const o = data.outcomes, f = data.flywheel, review = data.review;

  return (
    <div data-testid="screen-sales" className="max-w-6xl mx-auto">
      <div className="kz-fade-up">
        <p className="text-sm text-emerald-400 font-semibold tracking-wide">SALES & LEARNING</p>
        <h1 className="kz-head text-3xl md:text-4xl font-extrabold tracking-tight mt-2">Real outcomes, and what KURT learned.</h1>
        <p className="text-slate-400 mt-2 text-sm">Engaged → serious → joined → revenue. Never opens or impressions.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mt-8">
        <Funnel label="Engaged" value={o.engaged} accent="text-blue-400" />
        <Funnel label="Serious" value={o.serious} accent="text-amber-400" />
        <Funnel label="New members joined" value={o.joined} accent="text-emerald-400" />
        <Funnel label="MRR added" value={`£${o.mrr}`} accent="text-emerald-400" />
      </div>

      <div className="grid lg:grid-cols-4 gap-3 mt-4">
        <FlyStat icon={Users} label="Readers" value={f.readers.toLocaleString()} sub={`+${f.reader_growth_pct}% growth`} up />
        <FlyStat icon={Target} label="Member profile views" value={f.member_profile_views.toLocaleString()} />
        <FlyStat icon={ArrowUpRight} label="Outbound clicks to members" value={f.outbound_to_members.toLocaleString()} />
        <FlyStat icon={Flame} label="Reader growth" value={`${f.reader_growth_pct}%`} up />
      </div>

      {/* KURT self review */}
      <div className="rounded-2xl border border-white/10 bg-gradient-to-b from-[#141c2b] to-[#121824] p-6 mt-6 kz-fade-up">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2"><Brain size={18} className="text-emerald-400" /><h2 className="kz-head text-lg font-bold">KURT's weekly self-review</h2></div>
          <button data-testid="run-review-btn" onClick={runReview} disabled={reviewing}
            className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.06] text-slate-100 font-semibold text-sm hover:bg-white/[0.1] disabled:opacity-60 transition-all">
            {reviewing ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} className="text-emerald-400" />}
            {reviewing ? "Reviewing…" : "Run review"}
          </button>
        </div>
        {!review ? (
          <p className="text-sm text-slate-400">No review yet. Run one and KURT will kill losers, improve the uncertain, and scale winners.</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2">Learned</p>
              {(review.learned || []).map((l, i) => (
                <div key={i} className="mb-2 rounded-xl bg-[#0B0F17] border border-white/[0.06] p-3">
                  <p className="text-sm font-semibold text-slate-100">{l.title}</p>
                  <p className="text-xs text-slate-400 mt-1">{l.detail}</p>
                  {l.impact && <span className="text-[10px] text-emerald-400 font-bold">{l.impact}</span>}
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-emerald-400 font-semibold mb-2 flex items-center gap-1"><ArrowUpRight size={13} /> Scaling</p>
              {(review.scaling || []).map((s, i) => <p key={i} className="text-sm text-slate-300 mb-2 flex gap-1.5"><span className="text-emerald-400">•</span>{s}</p>)}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-red-400 font-semibold mb-2 flex items-center gap-1"><ArrowDownRight size={13} /> Killing</p>
              {(review.killing || []).map((s, i) => <p key={i} className="text-sm text-slate-300 mb-2 flex gap-1.5"><span className="text-red-400">•</span>{s}</p>)}
              {review.next_focus && <>
                <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-2 mt-3">Next focus</p>
                {review.next_focus.map((s, i) => <p key={i} className="text-sm text-slate-300 mb-1 flex gap-1.5"><span className="text-slate-500">→</span>{s}</p>)}
              </>}
            </div>
          </div>
        )}
      </div>

      {/* Experiment ledger */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-4"><CheckCircle2 size={18} className="text-emerald-400" /><h2 className="kz-head text-lg font-bold">Experiment ledger</h2></div>
        {data.ledger.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-[#121824] p-8 text-center text-sm text-slate-400">Nothing logged yet — create a play in Opportunities.</div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#121824] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-white/[0.07]">
                  <th className="p-3 font-semibold">Company</th><th className="p-3 font-semibold">Strategy</th><th className="p-3 font-semibold">Channel</th><th className="p-3 font-semibold">Result</th><th className="p-3 font-semibold">Confidence</th><th className="p-3 font-semibold">Record outcome</th>
                </tr>
              </thead>
              <tbody>
                {data.ledger.map((l) => <LedgerRow key={l.id} l={l} onSaved={load} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ReferralEngine key={refKey} />
      <SalesRecruitment onOnboarded={() => setRefKey((k) => k + 1)} />
    </div>
  );
};

const FlyStat = ({ icon: Icon, label, value, sub, up }) => (
  <div className="rounded-2xl border border-white/10 bg-[#121824] p-4 kz-fade-up">
    <Icon size={15} className="text-slate-500" />
    <div className="kz-head text-xl font-extrabold mt-2">{value}</div>
    <div className="text-[11px] text-slate-400 mt-0.5">{label}</div>
    {sub && <div className={`text-[10px] mt-1 font-semibold ${up ? "text-emerald-400" : "text-slate-500"}`}>{sub}</div>}
  </div>
);

const RESULTS = ["pending", "engaged", "serious", "joined", "lost"];
const RESULT_COLOR = { joined: "text-emerald-400", serious: "text-amber-400", engaged: "text-blue-400", lost: "text-red-400", pending: "text-slate-500" };

const LedgerRow = ({ l, onSaved }) => {
  const [result, setResult] = useState(l.result || "pending");
  const [revenue, setRevenue] = useState(l.revenue || 0);
  const [saving, setSaving] = useState(false);

  const save = async (nextResult, nextRev) => {
    setSaving(true);
    try {
      await api.setOutcome(l.id, nextResult, Number(nextRev) || 0);
      const label = nextResult === "joined" ? `Joined · £${nextRev} logged` : `Marked ${nextResult}`;
      toast.success(label);
      onSaved && onSaved();
    } catch { toast.error("Couldn't save outcome."); }
    finally { setSaving(false); }
  };

  return (
    <tr data-testid={`ledger-${l.id}`} className="border-b border-white/[0.04] last:border-0">
      <td className="p-3 text-slate-200">{l.company}</td>
      <td className="p-3 text-slate-400">{l.strategy}</td>
      <td className="p-3 text-slate-400">{l.channel}</td>
      <td className="p-3"><span className={`text-xs font-semibold ${RESULT_COLOR[result] || "text-slate-500"}`}>{result}</span></td>
      <td className="p-3 text-slate-400 kz-mono">{l.confidence}%</td>
      <td className="p-3">
        <div className="flex items-center gap-1.5">
          <select data-testid={`outcome-select-${l.id}`} value={result} disabled={saving}
            onChange={(e) => { setResult(e.target.value); if (e.target.value !== "joined") save(e.target.value, 0); }}
            className="bg-[#0B0F17] border border-white/10 rounded-lg px-2 py-1 text-xs text-slate-200 outline-none focus:border-emerald-500/40">
            {RESULTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {result === "joined" && (
            <>
              <div className="flex items-center bg-[#0B0F17] border border-white/10 rounded-lg px-2">
                <span className="text-xs text-slate-500">£</span>
                <input data-testid={`outcome-revenue-${l.id}`} type="number" min="0" value={revenue} onChange={(e) => setRevenue(e.target.value)}
                  className="w-16 bg-transparent text-xs text-slate-200 outline-none py-1 pl-1" placeholder="MRR" />
              </div>
              <button data-testid={`outcome-save-${l.id}`} onClick={() => save("joined", revenue)} disabled={saving}
                className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-emerald-500 text-[#04140d] hover:bg-emerald-400 disabled:opacity-60">Log</button>
            </>
          )}
          <button data-testid={`del-ledger-${l.id}`} onClick={async () => { await api.deleteLedger(l.id); onSaved && onSaved(); }}
            className="text-slate-600 hover:text-red-400 transition-colors ml-auto" title="Remove"><Trash2 size={13} /></button>
        </div>
      </td>
    </tr>
  );
};
